import { Chromosome } from '../types/algorithm';
import { calculateChromosomeFitness } from '../utils/fitnessCalculator';
import {
  initializePopulation,
  findBestChromosome,
} from './geneticAlgorithm';
import { SeededRNG, createSeededRNG } from '../utils/seededRNG';
import { incrementLocalSearchCalls } from '../utils/benchmarkInstrumentation';

/**
 * Shuffled Frog Leaping Algorithm (SFLA) implementation from scratch
 * Based on memeplex structure and local/global search
 */

/**
 * Divides population into memeplexes
 */
function createMemeplexes(
  population: Chromosome[],
  memeplexCount: number
): Chromosome[][] {
  const memeplexes: Chromosome[][] = Array(memeplexCount)
    .fill(null)
    .map(() => []);

  // Distribute frogs (chromosomes) round-robin style
  for (let i = 0; i < population.length; i++) {
    memeplexes[i % memeplexCount].push(population[i]);
  }

  return memeplexes;
}

/**
 * Shuffles memeplexes back into population using interleaving (classic SFLA approach)
 * Interleaves frogs from different memeplexes to maintain mixing effect
 */
function shuffleMemeplexes(
  memeplexes: Chromosome[][],
  population: Chromosome[]
): void {
  const memeplexCount = memeplexes.length;
  
  // Interleave frogs from memeplexes: for position i, use memeplex (i % memeplexCount)
  // and frog at position floor(i / memeplexCount) within that memeplex
  for (let i = 0; i < population.length; i++) {
    const memeplexIdx = i % memeplexCount;
    const frogIdx = Math.floor(i / memeplexCount);
    
    if (frogIdx < memeplexes[memeplexIdx].length) {
      population[i] = memeplexes[memeplexIdx][frogIdx];
    }
  }
}

/**
 * Updates worst frog in memeplex using local best
 * Fixed: Step size is bounded to prevent excessive copying
 * Fixed: Uses Set to avoid copying same gene multiple times
 */
function updateWorstFrog(
  worst: Chromosome,
  best: Chromosome,
  taskCount: number,
  serviceCount: number,
  matrix: number[][][],
  rng: SeededRNG
): Chromosome {
  incrementLocalSearchCalls();
  // Standard SFLA: step size is proportional to the distance (number of differing genes)
  const differingIndices: number[] = [];
  for (let i = 0; i < taskCount; i++) {
    if (worst.x[i] !== best.x[i]) differingIndices.push(i);
  }

  // At least move one gene; at most close the whole gap this iteration.
  const maxStep = Math.max(1, differingIndices.length);
  const step = rng.randomIntRange(1, maxStep);

  const newFrog: Chromosome = {
    x: [...worst.x],
    fitness: 0,
    rwp: 0,
  };

  // Copy 'step' genes from the best frog, chosen from the positions that differ
  rng.shuffle(differingIndices);
  const toCopy = differingIndices.slice(0, step);
  for (const idx of toCopy) {
    newFrog.x[idx] = best.x[idx];
  }

  calculateChromosomeFitness(newFrog, matrix, taskCount, serviceCount);
  return newFrog;
}

/**
 * SFLA evolution for one generation
 */
function evolveSFLA(
  population: Chromosome[],
  memeplexes: Chromosome[][],
  taskCount: number,
  serviceCount: number,
  matrix: number[][][],
  globalBest: Chromosome,
  rng: SeededRNG,
  localIterations: number
): void {
  for (const memeplex of memeplexes) {
    for (let iter = 0; iter < localIterations; iter++) {
      // Sort memeplex by fitness (best to worst)
      memeplex.sort((a, b) => b.fitness - a.fitness);
      const memeplexBest = memeplex[0];
      const memeplexWorst = memeplex[memeplex.length - 1];

      // Try to improve worst frog using memeplex best
      let improved = updateWorstFrog(
        memeplexWorst,
        memeplexBest,
        taskCount,
        serviceCount,
        matrix,
        rng
      );

      if (improved.fitness > memeplexWorst.fitness) {
        memeplex[memeplex.length - 1] = improved;
        continue;
      }

      // If not improved, try using global best
      improved = updateWorstFrog(
        memeplexWorst,
        globalBest,
        taskCount,
        serviceCount,
        matrix,
        rng
      );

      if (improved.fitness > memeplexWorst.fitness) {
        memeplex[memeplex.length - 1] = improved;
      } else {
        // If still not improved, replace with random solution using seeded RNG
        const randomFrog: Chromosome = {
          x: Array(taskCount)
            .fill(0)
            .map(() => rng.randomInt(serviceCount)),
          fitness: 0,
          rwp: 0,
        };
        calculateChromosomeFitness(randomFrog, matrix, taskCount, serviceCount);
        memeplex[memeplex.length - 1] = randomFrog;
      }
    }
  }
}

/**
 * Runs SFLA for specified generations
 */
export async function runSFLA(
  taskCount: number,
  serviceCount: number,
  matrix: number[][][],
  parameters: {
    populationSize: number;
    generations: number;
    memeplexCount: number;
    localIterations?: number; // Number of worst-frog refinements per memeplex per generation (standard SFLA: >1)
  },
  onGenerationComplete?: (generation: number, bestFitness: number) => void,
  seed?: number,
  runtimeOptions?: {
    yieldForProgress?: boolean;
    reportEveryGeneration?: boolean;
  }
): Promise<Chromosome> {
  // Create seeded RNG for reproducibility
  const rng = createSeededRNG(seed);
  const shouldYieldForProgress = runtimeOptions?.yieldForProgress ?? true;
  const shouldReportEveryGeneration = runtimeOptions?.reportEveryGeneration ?? false;
  
  // Initialize population
  let population = initializePopulation(
    parameters.populationSize,
    taskCount,
    serviceCount,
    rng
  );

  // Evaluate initial population
  for (const chrom of population) {
    calculateChromosomeFitness(chrom, matrix, taskCount, serviceCount);
  }

  // Sort population by fitness (DESC) before forming memeplexes
  population.sort((a, b) => b.fitness - a.fitness);
  
  // Create memeplexes
  let memeplexes = createMemeplexes(population, parameters.memeplexCount);

  // Evolution loop
  let lastUpdateTime = Date.now();
  const UPDATE_INTERVAL = 100; // Update UI every 100ms (PERFORMANCE OPTIMIZATION)
  const localIterations = Math.max(1, parameters.localIterations ?? 5); // Default Ns = 5 inner iterations

  for (let gen = 0; gen < parameters.generations; gen++) {
    // Find global best
    const globalBest = findBestChromosome(population);

    // Evolve each memeplex
    evolveSFLA(
      population,
      memeplexes,
      taskCount,
      serviceCount,
      matrix,
      globalBest,
      rng,
      localIterations
    );

    // Shuffle memeplexes (redistribute)
    shuffleMemeplexes(memeplexes, population);

    // Sort population by fitness (DESC) after shuffle
    population.sort((a, b) => b.fitness - a.fitness);
    
    // Recreate memeplexes for next generation
    memeplexes = createMemeplexes(population, parameters.memeplexCount);

    // Batch UI updates - only update every 100ms or every 10 generations (PERFORMANCE OPTIMIZATION)
    const now = Date.now();
    const shouldUpdate =
      shouldReportEveryGeneration ||
      (now - lastUpdateTime > UPDATE_INTERVAL) ||
      (gen % 10 === 9) ||
      (gen === parameters.generations - 1);
    
    if (shouldUpdate) {
      const best = findBestChromosome(population);
      if (onGenerationComplete) {
        onGenerationComplete(gen + 1, best.fitness);
      }
      lastUpdateTime = now;
      if (shouldYieldForProgress) {
        // Yield control to allow UI updates when running interactively.
        await new Promise(resolve => setTimeout(resolve, 0));
      }
    }
  }

  return findBestChromosome(population);
}
